import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  getDoc,
  setDoc,
  deleteDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { LoyaltyProgram } from '../types';

/**
 * Professional Firestore service for loyalty program management
 * This service provides clean, type-safe database operations
 */
export class FirestoreService {
  private static readonly COLLECTION_NAME = 'loyaltyPrograms';

  /**
   * Add a new loyalty program for a user
   */
  static async addLoyaltyProgram(
    userId: string, 
    program: Omit<LoyaltyProgram, 'id'>
  ): Promise<LoyaltyProgram> {
    try {
      console.log('🔥 Firestore: Adding loyalty program for user:', userId);
      console.log('🔥 Firestore: Program data:', program);

      const programData = {
        ...program,
        userId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), programData);
      
      const savedProgram: LoyaltyProgram = {
        ...program,
        id: docRef.id,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      console.log('✅ Firestore: Program added successfully with ID:', docRef.id);
      return savedProgram;
    } catch (error) {
      console.error('❌ Firestore: Error adding loyalty program:', error);
      throw new Error(`Failed to add loyalty program: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get all loyalty programs for a user
   */
  static async getUserLoyaltyPrograms(userId: string): Promise<LoyaltyProgram[]> {
    try {
      console.log('🔥 Firestore: Loading loyalty programs for user:', userId);

      // Simple query to avoid index requirements
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('userId', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      const programs: LoyaltyProgram[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        programs.push({
          id: doc.id,
          name: data.name,
          type: data.type,
          logo: data.logo,
          currency: data.currency,
          points: data.points,
          tier: data.tier,
          tierColor: data.tierColor,
          color: data.color,
          website: data.website,
          redemptionUrl: data.redemptionUrl,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          // Credit card specific fields
          cardNumber: data.cardNumber,
          cardType: data.cardType,
          expiryDate: data.expiryDate?.toDate(),
        });
      });

      // Sort client-side by creation date (newest first)
      programs.sort((a, b) => {
        const dateA = a.createdAt || new Date(0);
        const dateB = b.createdAt || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      console.log(`✅ Firestore: Loaded ${programs.length} loyalty programs`);
      programs.forEach(program => {
        console.log(`  - ${program.name} (${program.type})`);
      });

      return programs;
    } catch (error) {
      console.error('❌ Firestore: Error loading loyalty programs:', error);
      throw new Error(`Failed to load loyalty programs: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get loyalty programs by type for a user
   * Uses client-side filtering to avoid complex Firestore indexes
   */
  static async getUserLoyaltyProgramsByType(
    userId: string, 
    type: 'credit_card' | 'airline' | 'hotel'
  ): Promise<LoyaltyProgram[]> {
    try {
      console.log(`🔥 Firestore: Loading ${type} programs for user:`, userId);

      // Get all programs for the user, then filter client-side to avoid composite index requirement
      const allPrograms = await this.getUserLoyaltyPrograms(userId);
      const filteredPrograms = allPrograms.filter(program => program.type === type);

      console.log(`✅ Firestore: Loaded ${filteredPrograms.length} ${type} programs (filtered from ${allPrograms.length} total)`);
      return filteredPrograms;
    } catch (error) {
      console.error(`❌ Firestore: Error loading ${type} programs:`, error);
      throw new Error(`Failed to load ${type} programs: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Update a loyalty program
   */
  static async updateLoyaltyProgram(
    programId: string, 
    updates: Partial<Omit<LoyaltyProgram, 'id' | 'createdAt'>>
  ): Promise<void> {
    try {
      console.log('🔥 Firestore: Updating loyalty program:', programId);
      
      const docRef = doc(db, this.COLLECTION_NAME, programId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });

      console.log('✅ Firestore: Program updated successfully');
    } catch (error) {
      console.error('❌ Firestore: Error updating loyalty program:', error);
      throw new Error(`Failed to update loyalty program: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Delete a loyalty program
   */
  static async deleteLoyaltyProgram(programId: string): Promise<void> {
    try {
      console.log('🔥 Firestore: Deleting loyalty program:', programId);
      
      const docRef = doc(db, this.COLLECTION_NAME, programId);
      await deleteDoc(docRef);

      console.log('✅ Firestore: Program deleted successfully');
    } catch (error) {
      console.error('❌ Firestore: Error deleting loyalty program:', error);
      throw new Error(`Failed to delete loyalty program: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Check if Firestore is properly configured
   */
  static async healthCheck(): Promise<boolean> {
    try {
      // Try to read from the collection (this will succeed even if empty)
      const q = query(collection(db, this.COLLECTION_NAME));
      await getDocs(q);
      console.log('✅ Firestore: Health check passed');
      return true;
    } catch (error) {
      console.error('❌ Firestore: Health check failed:', error);
      return false;
    }
  }

  /**
   * User Profile Management
   */

  /**
   * Save user profile data to Firestore
   */
  static async saveUserProfile(
    userId: string, 
    profileData: {
      name: string;
      email: string;
      phone: string;
      dateOfBirth: string;
      address: string;
      avatar?: string;
    }
  ): Promise<void> {
    try {
      console.log('🔥 Firestore: Saving user profile for:', userId);
      console.log('🔥 Firestore: Profile data being saved:', profileData);
      
      const userDocRef = doc(db, 'users', userId);
      
      // Check if document exists first
      const docSnapshot = await getDoc(userDocRef);
      console.log('🔥 Firestore: Document exists?', docSnapshot.exists());
      
      // Filter out undefined values to avoid Firestore errors
      const cleanedProfileData = Object.fromEntries(
        Object.entries(profileData).filter(([_, value]) => value !== undefined)
      );
      console.log('🔥 Firestore: Cleaned profile data (no undefined):', cleanedProfileData);
      
      const profileUpdate = {
        profile: cleanedProfileData,
        updatedAt: Timestamp.now(),
      };

      // Use setDoc with merge to create document if it doesn't exist
      await setDoc(userDocRef, profileUpdate, { merge: true });
      console.log('✅ Firestore: User profile saved successfully');
    } catch (error) {
      console.error('❌ Firestore: Error saving user profile:', error);
      console.error('❌ Firestore: Error details:', error);
      throw new Error(`Failed to save user profile: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get user profile data from Firestore
   */
  static async getUserProfile(userId: string): Promise<any | null> {
    try {
      console.log('🔥 Firestore: Fetching user profile for:', userId);
      
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        console.log('📝 Firestore: No profile found for user');
        return null;
      }

      const userData = userDoc.data();
      console.log('🔥 Firestore: Full user document:', userData);
      console.log('🔥 Firestore: Profile field:', userData.profile);
      console.log('✅ Firestore: User profile fetched successfully');
      return userData.profile || null;
    } catch (error) {
      console.error('❌ Firestore: Error fetching user profile:', error);
      return null;
    }
  }

  /**
   * Save user preferences to Firestore
   */
  static async saveUserPreferences(
    userId: string,
    preferences: {
      notifications: {
        email: boolean;
        sms: boolean;
        push: boolean;
        deals: boolean;
        expiry: boolean;
        transfers: boolean;
      };
      privacy: {
        dataCollection: boolean;
        personalization: boolean;
        marketing: boolean;
        thirdParty: boolean;
      };
      theme: 'light' | 'dark';
    }
  ): Promise<void> {
    try {
      console.log('🔥 Firestore: Saving user preferences for:', userId);
      console.log('🔥 Firestore: Preferences data being saved:', preferences);
      
      const userDocRef = doc(db, 'users', userId);
      
      // Check if document exists first
      const docSnapshot = await getDoc(userDocRef);
      console.log('🔥 Firestore: Document exists?', docSnapshot.exists());
      
      const preferencesUpdate = {
        preferences,
        preferencesUpdatedAt: Timestamp.now(),
      };

      // Use setDoc with merge to create document if it doesn't exist
      await setDoc(userDocRef, preferencesUpdate, { merge: true });
      console.log('✅ Firestore: User preferences saved successfully');
    } catch (error) {
      console.error('❌ Firestore: Error saving user preferences:', error);
      console.error('❌ Firestore: Error details:', error);
      throw new Error(`Failed to save user preferences: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get user preferences from Firestore
   */
  static async getUserPreferences(userId: string): Promise<any | null> {
    try {
      console.log('🔥 Firestore: Fetching user preferences for:', userId);
      
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        console.log('📝 Firestore: No preferences found for user');
        return null;
      }

      const userData = userDoc.data();
      console.log('🔥 Firestore: Full user document for preferences:', userData);
      console.log('🔥 Firestore: Preferences field:', userData.preferences);
      console.log('✅ Firestore: User preferences fetched successfully');
      return userData.preferences || null;
    } catch (error) {
      console.error('❌ Firestore: Error fetching user preferences:', error);
      return null;
    }
  }

  /**
   * Initialize user document when they first sign up
   */
  static async initializeUserDocument(
    userId: string,
    userData: {
      email: string;
      displayName: string;
      photoURL?: string;
    }
  ): Promise<void> {
    try {
      console.log('🔥 Firestore: Initializing user document for:', userId);
      
      const userDocRef = doc(db, 'users', userId);
      
      // Check if document already exists
      const existingDoc = await getDoc(userDocRef);
      
      if (existingDoc.exists()) {
        const existingData = existingDoc.data();
        console.log('📝 Firestore: User document exists, checking for missing fields');
        
        // Update basic auth info and ensure profile/preferences exist
        const updateData: any = {
          displayName: userData.displayName,
          email: userData.email,
          photoURL: userData.photoURL,
          updatedAt: Timestamp.now(),
        };

        // Add profile if it doesn't exist
        if (!existingData.profile) {
          console.log('🔥 Firestore: Adding missing profile field');
          updateData.profile = {
            name: userData.displayName || '',
            email: userData.email,
            phone: '',
            dateOfBirth: '',
            address: '',
            avatar: userData.photoURL || '',
          };
        }

        // Add preferences if they don't exist
        if (!existingData.preferences) {
          console.log('🔥 Firestore: Adding missing preferences field');
          updateData.preferences = {
            notifications: {
              email: true,
              sms: false,
              push: true,
              deals: true,
              expiry: true,
              transfers: false,
            },
            privacy: {
              dataCollection: true,
              personalization: true,
              marketing: false,
              thirdParty: false,
            },
            theme: 'light',
          };
        }

        await updateDoc(userDocRef, updateData);
        console.log('✅ Firestore: User document updated with missing fields');
        return;
      }
      
      // Create new document with defaults
      console.log('🔥 Firestore: Creating new user document');
      const initialUserData = {
        ...userData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        profile: {
          name: userData.displayName || '',
          email: userData.email,
          phone: '',
          dateOfBirth: '',
          address: '',
          avatar: userData.photoURL || '',
        },
        preferences: {
          notifications: {
            email: true,
            sms: false,
            push: true,
            deals: true,
            expiry: true,
            transfers: false,
          },
          privacy: {
            dataCollection: true,
            personalization: true,
            marketing: false,
            thirdParty: false,
          },
          theme: 'light',
        },
      };

      await setDoc(userDocRef, initialUserData);
      console.log('✅ Firestore: New user document created successfully');
    } catch (error) {
      console.error('❌ Firestore: Error initializing user document:', error);
      throw new Error('Failed to initialize user document');
    }
  }

  /**
   * Manual function to fix existing user documents that are missing profile/preferences
   */
  static async fixUserDocument(userId: string): Promise<void> {
    try {
      console.log('🔧 Firestore: Fixing user document for:', userId);
      
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        console.log('❌ Firestore: User document does not exist');
        return;
      }

      const userData = userDoc.data();
      const updateData: any = {};

      // Add profile if it doesn't exist
      if (!userData.profile) {
        console.log('🔧 Firestore: Adding missing profile field');
        updateData.profile = {
          name: userData.displayName || 'John Doe',
          email: userData.email || '',
          phone: '',
          dateOfBirth: '',
          address: '',
          avatar: userData.photoURL || '',
        };
      }

      // Add preferences if they don't exist
      if (!userData.preferences) {
        console.log('🔧 Firestore: Adding missing preferences field');
        updateData.preferences = {
          notifications: {
            email: true,
            sms: false,
            push: true,
            deals: true,
            expiry: true,
            transfers: false,
          },
          privacy: {
            dataCollection: true,
            personalization: true,
            marketing: false,
            thirdParty: false,
          },
          theme: 'light',
        };
      }

      if (Object.keys(updateData).length > 0) {
        updateData.updatedAt = Timestamp.now();
        await updateDoc(userDocRef, updateData);
        console.log('✅ Firestore: User document fixed successfully');
      } else {
        console.log('✅ Firestore: User document already has all required fields');
      }
    } catch (error) {
      console.error('❌ Firestore: Error fixing user document:', error);
      throw error;
    }
  }

  /**
   * Get user data including role and permissions
   */
  static async getUserData(userId: string): Promise<any> {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        return userSnap.data();
      }
      return null;
    } catch (error) {
      console.error('❌ Firestore: Error getting user data:', error);
      throw error;
    }
  }

  /**
   * Update user role and permissions
   */
  static async updateUserRole(userId: string, role: string, permissions: any): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role,
        permissions,
        updatedAt: Timestamp.now(),
      });
      console.log('✅ Firestore: User role updated:', userId, role);
    } catch (error) {
      console.error('❌ Firestore: Error updating user role:', error);
      throw error;
    }
  }

  /**
   * Log admin action
   */
  static async logAdminAction(
    adminId: string,
    action: string,
    targetId: string,
    targetType: string,
    changes?: Record<string, any>
  ): Promise<void> {
    try {
      const adminLogsRef = collection(db, 'adminLogs');
      const logData = {
        adminId,
        adminEmail: '', // Will be populated from auth
        action,
        targetId,
        targetType,
        changes: changes || {},
        timestamp: Timestamp.now(),
        details: `${action} on ${targetType} ${targetId}`,
      };

      await addDoc(adminLogsRef, logData);
      console.log('✅ Firestore: Admin action logged:', action);
    } catch (error) {
      console.error('❌ Firestore: Error logging admin action:', error);
      throw error;
    }
  }
}

export default FirestoreService;